<template>
  <v-card class="mb-8 pa-0 elevation-2">
    <v-card-title class="text-h5 font-weight-bold pa-6 pb-4">
      <v-icon left color="primary" class="mr-3">mdi-account-group</v-icon>
      Partenaires
    </v-card-title>
    
    <v-divider></v-divider>
    
    <v-card-text class="pa-6">
      <!-- Top Stats Row -->
      <v-row class="mb-6">
        <v-col cols="12" md="4">
          <div class="stat-card stat-card-data">
            <div class="stat-left">
              <div class="stat-label">Données<br>remontées</div>
            </div>
            <div class="stat-center">
              <div class="text-h4 font-weight-bold stat-value">{{ formatDataUploaded }}</div>
              <div class="text-caption text--secondary">lignes</div>
            </div>
            <div class="stat-right" v-if="dataUploadedTrend !== null">
              <v-icon :color="dataUploadedTrendColor">{{ dataUploadedTrendIcon }}</v-icon>
              <span class="trend-value">{{ Math.abs(dataUploadedTrend) }}%</span>
            </div>
          </div>
        </v-col>
        
        <v-col cols="12" md="4">
          <div class="stat-card stat-card-revenue">
            <div class="stat-left">
              <div class="stat-label">CA</div>
            </div>
            <div class="stat-center">
              <div class="text-h4 font-weight-bold stat-value">{{ formatRevenue }}</div>
            </div>
            <div class="stat-right" v-if="revenueTrend !== null">
              <v-icon :color="revenueTrendColor">{{ revenueTrendIcon }}</v-icon>
              <span class="trend-value">{{ Math.abs(revenueTrend) }}%</span>
            </div>
          </div>
        </v-col>
        
        <v-col cols="12" md="4">
          <div class="stat-card stat-card-partners">
            <div class="stat-left">
              <div class="stat-label">Partenaires<br>Actifs</div>
            </div>
            <div class="stat-center">
              <div class="text-h2 font-weight-bold stat-value">{{ activePartners }}</div>
            </div>
          </div>
        </v-col>
      </v-row>
      
      <!-- Bottom Charts Row -->
      <v-row>
        <v-col cols="12" md="6">
          <div class="chart-card">
            <div class="chart-header">
              <v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
              <h3 class="text-h6 font-weight-bold">Problème de remontée de données</h3>
            </div>
            
            <!-- Dynamic Partner Charts -->
            <div v-if="dataIssuesPartners.length > 0">
              <div 
                v-for="partner in visibleDataIssuesPartners" 
                :key="partner.id"
                class="partner-chart-item">
                <div class="partner-name">{{ partner.name }}</div>
                <BarChartFixed 
                  :bars="generateBarsFromData(partner.data)"
                  :tooltip-style="tooltipStyle"
                />
              </div>
              
              <!-- Expand/Collapse Button -->
              <div v-if="dataIssuesPartners.length > 3" class="text-center mt-3">
                <v-btn
                  text
                  small
                  color="primary"
                  @click="expandedSections.dataIssues = !expandedSections.dataIssues"
                >
                  <v-icon left small>
                    {{ expandedSections.dataIssues ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                  </v-icon>
                  {{ expandedSections.dataIssues ? 'Voir moins' : `Voir ${dataIssuesPartners.length - 3} de plus` }}
                </v-btn>
              </div>
            </div>
            
            <!-- Loading State -->
            <div v-else-if="loading" class="text-center py-8">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
              <div class="mt-3 text--secondary">Chargement des données...</div>
            </div>
            
            <!-- Empty State -->
            <div v-else class="text-center py-8 text--secondary">
              <v-icon large class="mb-3">mdi-check-circle</v-icon>
              <div>Aucun problème de remontée détecté</div>
            </div>
          </div>
        </v-col>
        
        <v-col cols="12" md="6">
          <div class="chart-card">
            <div class="chart-header">
              <v-icon color="warning" class="mr-2">mdi-trending-down</v-icon>
              <h3 class="text-h6 font-weight-bold">Baisse importante de CA</h3>
            </div>
            
            <!-- Dynamic CA Drop Charts -->
            <div v-if="caDropPartners.length > 0">
              <div 
                v-for="partner in visibleCADropPartners" 
                :key="partner.id"
                class="partner-chart-item">
                <div class="partner-name">{{ partner.name }}</div>
                <BarChartFixed 
                  :bars="generateBarsFromData(partner.data, 'revenue')"
                  :tooltip-style="tooltipStyle"
                />
              </div>
              
              <!-- Expand/Collapse Button -->
              <div v-if="caDropPartners.length > 3" class="text-center mt-3">
                <v-btn
                  text
                  small
                  color="primary"
                  @click="expandedSections.caDrops = !expandedSections.caDrops"
                >
                  <v-icon left small>
                    {{ expandedSections.caDrops ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                  </v-icon>
                  {{ expandedSections.caDrops ? 'Voir moins' : `Voir ${caDropPartners.length - 3} de plus` }}
                </v-btn>
              </div>
            </div>
            
            <!-- Loading State -->
            <div v-else-if="loading" class="text-center py-8">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
              <div class="mt-3 text--secondary">Chargement des données...</div>
            </div>
            
            <!-- Empty State -->
            <div v-else class="text-center py-8 text--secondary">
              <v-icon large class="mb-3">mdi-check-circle</v-icon>
              <div>Aucune baisse importante détectée</div>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import HoverTooltip from '@/components/common/HoverTooltip.vue';
import BarChartFixed from '@/components/admin/data-availability/charts/BarChartFixed.vue';
import { formatCurrencyCompact, formatNumberCompact } from '@/utils/formatting';
import { mdiArrowTopRight, mdiArrowBottomLeft } from '@mdi/js';

/**
 * Expected API data structure:
 * {
 *   stats: {
 *     dataUploaded: {
 *       value: 1235,
 *       trend: -10,
 *       trendDirection: "down"
 *     },
 *     revenue: {
 *       value: 540000,
 *       trend: 5,
 *       trendDirection: "up"
 *     },
 *     activePartners: {
 *       value: 76
 *     }
 *   },
 *   dataPartnersIssues: {
 *     dataDrop: [
 *       {
 *         id: 1,
 *         name: "MoneyTag",
 *         data: [
 *           {
 *             date: "2024-01-01",
 *             value: 85,
 *             siteCount: 12,
 *             hasIssue: false
 *           },
 *           {
 *             date: "2024-01-02",
 *             value: 0,
 *             siteCount: 0,
 *             hasIssue: true
 *           }
 *         ]
 *       }
 *     ],
 *     caDrop: [
 *       {
 *         id: 2,
 *         name: "Digiteka",
 *         data: [
 *           {
 *             date: "2024-01-01",
 *             value: 540,
 *             revenue: 540000,
 *             hasIssue: false
 *           },
 *           {
 *             date: "2024-01-02",
 *             value: 320,
 *             revenue: 320000,
 *             hasIssue: true
 *           }
 *         ]
 *       }
 *     ]
 *   }
 * }
 */
export default {
  name: 'PartnersOverviewCard',
  components: {
    HoverTooltip,
    BarChartFixed
  },
  props: {
    data: {
      type: Object,
      default: () => ({})
    },
    loading: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      icons: {
        mdiArrowTopRight,
        mdiArrowBottomLeft
      },
      tooltipStyle: {
        backgroundColor: '#fff',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        padding: '8px 12px',
        fontSize: '14px',
        color: '#333',
        whiteSpace: 'nowrap'
      },
      dataPartnersIssues: {
        dataDrop: []
      },
      expandedSections: {
        dataIssues: false,
        caDrops: false
      }
    };
  },
  methods: {
    getRandomHeight() {
      // Generate random heights between 30 and 90 for bar charts
      return Math.floor(Math.random() * 60) + 30;
    },
    getRandomDecreasingHeight(index) {
      // Generate decreasing trend with some randomness
      const baseHeight = 90 - (index * 2.2);
      const randomVariation = Math.random() * 15 - 7.5;
      return Math.max(20, Math.min(90, baseHeight + randomVariation));
    },
    getBarTooltip(index) {
      // Generate tooltip with date and site count
      const date = new Date();
      date.setDate(date.getDate() - (30 - index));
      const dateStr = date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
      const siteCount = Math.floor(Math.random() * 15) + 1;
      return `${dateStr} - ${siteCount} site${siteCount > 1 ? 's' : ''}`;
    },
    generateBars(count, type = 'random', lastBlinking = false) {
      const bars = [];
      for (let i = 1; i <= count; i++) {
        const height = type === 'decreasing' 
          ? this.getRandomDecreasingHeight(i) 
          : this.getRandomHeight();
        
        bars.push({
          height,
          tooltip: this.getBarTooltip(i),
          blinking: lastBlinking && i === count,
          color: '#56ca00'
        });
      }
      return bars;
    },
    generateBarsFromData(partnerData, type = 'sites') {
      // Convert API data to bar chart format
      if (!partnerData || !Array.isArray(partnerData)) {
        return this.generateBars(30, 'random', true); // Fallback to random data
      }
      
      const bars = partnerData.map((dataPoint, index) => {
        const isLastBar = index === partnerData.length - 1;
        const hasIssue = dataPoint.hasIssue || dataPoint.value === 0;
        
        let value;
        if (type === 'revenue') {
          // For CA Drop, use the value directly (it's the revenue)
          value = dataPoint.value || 0;
        } else {
          // For Data Drop, use siteCount as value if value is not present
          value = dataPoint.value !== undefined ? dataPoint.value : (dataPoint.siteCount || 0);
        }
        
        const bar = {
          height: type === 'revenue' ? this.normalizeRevenueHeight(value) : this.normalizeHeight(value),
          tooltip: this.formatDataTooltip(dataPoint, type),
          blinking: isLastBar && hasIssue,
          color: hasIssue ? '#ff2000' : '#56ca00'
        };
        
        return bar;
      });
      
      return bars;
    },
    normalizeHeight(value) {
      // Handle zero values
      if (value === 0) {
        return 5; // Minimum visible height for zero values
      }
      
      // For site counts, we'll use a logarithmic scale for better visual distribution
      // This helps show differences between small and large values
      
      // Add 1 to avoid log(0) issues
      const logValue = Math.log10(value + 1);
      
      // Typical site counts range from 0 to ~50
      // log10(1) = 0, log10(10) ≈ 1, log10(50) ≈ 1.7, log10(100) = 2
      const maxExpectedLog = Math.log10(51); // ≈ 1.7
      
      // Map the log value to height percentage
      // We want a range from 15% (minimum for non-zero) to 85% (maximum)
      const minHeight = 15;
      const maxHeight = 85;
      
      // Calculate normalized height
      const normalizedLog = Math.min(logValue / maxExpectedLog, 1); // Cap at 1
      const height = minHeight + (normalizedLog * (maxHeight - minHeight));
      
      return Math.round(height);
    },
    normalizeRevenueHeight(value) {
      // Handle zero or very small revenue values
      if (value <= 0) {
        return 5; // Minimum visible height
      }
      
      // For revenue values, we expect them to be in the thousands or hundreds of thousands
      // Use logarithmic scale for better distribution
      const logValue = Math.log10(value + 1);
      
      // Typical revenue might range from 0 to 1,000,000
      // log10(1000) = 3, log10(100000) = 5, log10(1000000) = 6
      const maxExpectedLog = 6; // log10(1,000,000)
      
      // Map to height percentage
      const minHeight = 15;
      const maxHeight = 85;
      
      const normalizedLog = Math.min(logValue / maxExpectedLog, 1);
      const height = minHeight + (normalizedLog * (maxHeight - minHeight));
      
      return Math.round(height);
    },
    formatDataTooltip(dataPoint, type = 'sites') {
      const date = dataPoint.date ? new Date(dataPoint.date).toLocaleDateString('fr-FR', { 
        day: '2-digit', 
        month: '2-digit' 
      }) : 'N/A';
      
      if (type === 'revenue') {
        // Format revenue values with currency
        const revenue = dataPoint.value || 0;
        const formattedRevenue = new Intl.NumberFormat('fr-FR', {
          style: 'currency',
          currency: 'EUR',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(revenue);
        return `${date} - CA: ${formattedRevenue}`;
      }
      
      if (dataPoint.siteCount !== undefined) {
        return `${date} - ${dataPoint.siteCount} site${dataPoint.siteCount > 1 ? 's' : ''}`;
      }
      
      if (dataPoint.value !== undefined) {
        return `${date} - Valeur: ${dataPoint.value}`;
      }
      
      return `${date} - ${dataPoint.label || 'Données'}`;
    }
  },
  computed: {
    dataIssuesPartners() {
      // Get partners with data drop issues from API response
      if (this.data && this.data.dataPartnersIssues && this.data.dataPartnersIssues.dataDrop) {
        return this.data.dataPartnersIssues.dataDrop;
      }
      return [];
    },
    caDropPartners() {
      // Get partners with CA drop issues from API response
      if (this.data && this.data.dataPartnersIssues && this.data.dataPartnersIssues.caDrop) {
        return this.data.dataPartnersIssues.caDrop;
      }
      return [];
    },
    visibleDataIssuesPartners() {
      // Show only first 3 partners unless expanded
      if (this.expandedSections.dataIssues || this.dataIssuesPartners.length <= 3) {
        return this.dataIssuesPartners;
      }
      return this.dataIssuesPartners.slice(0, 3);
    },
    visibleCADropPartners() {
      // Show only first 3 partners unless expanded
      if (this.expandedSections.caDrops || this.caDropPartners.length <= 3) {
        return this.caDropPartners;
      }
      return this.caDropPartners.slice(0, 3);
    },
    // Header stats computed properties
    formatDataUploaded() {
      const value = this.data?.stats?.dataUploaded?.value || 0;
      return formatNumberCompact(value, 0);
    },
    dataUploadedTrend() {
      return this.data?.stats?.dataUploaded?.trend || null;
    },
    dataUploadedTrendColor() {
      if (this.dataUploadedTrend === null) return 'grey';
      return this.dataUploadedTrend >= 0 ? 'success' : 'error';
    },
    dataUploadedTrendIcon() {
      if (this.dataUploadedTrend === null) return '';
      const direction = this.data?.stats?.dataUploaded?.trendDirection || (this.dataUploadedTrend >= 0 ? 'up' : 'down');
      return direction === 'up' ? this.icons.mdiArrowTopRight : this.icons.mdiArrowBottomLeft;
    },
    formatRevenue() {
      const value = this.data?.stats?.revenue?.value || 0;
      return formatCurrencyCompact(value, 0);
    },
    revenueTrend() {
      return this.data?.stats?.revenue?.trend || null;
    },
    revenueTrendColor() {
      if (this.revenueTrend === null) return 'grey';
      return this.revenueTrend >= 0 ? 'success' : 'error';
    },
    revenueTrendIcon() {
      if (this.revenueTrend === null) return '';
      const direction = this.data?.stats?.revenue?.trendDirection || (this.revenueTrend >= 0 ? 'up' : 'down');
      return direction === 'up' ? this.icons.mdiArrowTopRight : this.icons.mdiArrowBottomLeft;
    },
    activePartners() {
      return this.data?.stats?.activePartners?.value || 0;
    }
  },
  watch: {
    data: {
      handler(newData) {
        if (newData && newData.dataPartnersIssues) {
          this.dataPartnersIssues = newData.dataPartnersIssues;
        }
      },
      deep: true,
      immediate: true
    }
  }
};
</script>

<style scoped>
/* Stat cards with modern styling */
.stat-card {
  background-color: #f4f5fa;
  padding: 20px 24px;
  border-radius: 8px;
  transition: all 0.3s ease;
  height: 100%;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border: 1px solid #e0e6ee;
  box-shadow: 0 0 0px #e0e0e0;
  min-height: 100px;
  cursor: pointer;
}

.stat-card:hover {
  background-color: #f4f6ff;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15);
}

.stat-card:active {
  background-color: #f4f5fa;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.15);
}

.stat-left {
  flex-shrink: 0;
  min-width: 80px;
}

.stat-label {
  font-size: 14px;
  font-weight: 600;
  color: #666;
  line-height: 1.3;
}

.stat-center {
  flex: 1;
  text-align: center;
  padding: 0 16px;
}

.stat-right {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 4px;
}

.stat-value {
  color: #1a1a1a;
  margin-bottom: 4px;
}

.trend-value {
  font-size: 14px;
  font-weight: 600;
  color: #2e7d32;
}

/* Chart cards styling */
.chart-card {
  border: 1px solid #e0e6ee;
  background-color: #ffffff;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 0 0px #e0e0e0;
  height: 100%;
  transition: all 0.3s ease;
}

.chart-card:hover {
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15);
}

/* Partner chart items */
.partner-chart-item {
  margin-bottom: 20px;
  padding: 20px;
  background-color: #f4f5fa;
  border-radius: 8px;
  transition: all 0.3s ease;
  border: 1px solid #e0e6ee;
  box-shadow: 0 0 0px #e0e0e0;
}

.partner-chart-item:hover {
  background-color: #f4f6ff;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
}

.partner-chart-item:last-child {
  margin-bottom: 0;
}

.chart-header {
  display: flex;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
}

.partner-name {
  font-weight: 600;
  font-size: 0.875rem;
  color: #565656;
  margin-bottom: 12px;
}

/* Removed chart styling - now in GenericBarChart component */

/* Responsive adjustments */
@media (max-width: 960px) {
  .stat-card {
    margin-bottom: 16px;
  }
  
  .chart-card {
    margin-bottom: 16px;
  }
}
</style>